import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const LOGO_URL = "/amazonico-logo.png";

const SOCIAL_LINKS = [
  { icon: Instagram, href: "https://www.instagram.com/amazonico3000", label: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/amazonico3000", label: "Facebook" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/amazonico-3000", label: "LinkedIn" },
  { icon: Youtube, href: "https://www.youtube.com/@amazonico3000", label: "YouTube" },
];

/**
 * Amazonico 3000 marketing top-nav — used by the public homepage.
 * Beige/gold theme, sticky, transparent on hero → solid on scroll.
 * Anchors to on-page sections + external routes.
 */
export default function MainNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { label: "Home", href: "/business" },
    { label: "Filiere", href: "/business#categorie" },
    { label: "Marketplace", href: "/b2b/wines" },
    { label: "Rivista", href: "/rivista" },
    { label: "Ricette", href: "/ai-ricette" },
    { label: "Piani", href: "/business#piani" },
    { label: "Gestionale", href: "/gestionale" },
    { label: "Contatti", href: "/supporto" },
    { label: "Cambia sezione", href: "/" },
  ];

  const anchorClick = (href) => (e) => {
    // Smooth-scroll for /business#anchor when already on /business
    if (href.includes("#") && loc.pathname === href.split("#")[0]) {
      e.preventDefault();
      const id = href.split("#")[1];
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <nav
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? "bg-[#faf7f0]/95 backdrop-blur-md border-b border-[#C9A961]/30 shadow-sm"
          : "bg-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          {/* Logo — trasparente, wordmark rimosso a monte */}
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img
              src={LOGO_URL}
              alt="Amazonico 3000"
              className={`transition-all duration-300 object-contain ${scrolled ? "h-11 md:h-12" : "h-14 md:h-16"}`}
            />
          </Link>

          {/* Desktop menu */}
          <div className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              l.href.includes("#")
                ? <a key={l.href} href={l.href} onClick={anchorClick(l.href)} className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-stone-700 hover:text-[#A88A4A] font-semibold transition-colors">{l.label}</a>
                : <Link key={l.href} to={l.href} className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-stone-700 hover:text-[#A88A4A] font-semibold transition-colors">{l.label}</Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Social icons (desktop only) */}
            <div className="hidden xl:flex items-center gap-1 mr-2 pr-3 border-r border-stone-300">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-social-${s.label.toLowerCase()}`}
                  aria-label={s.label}
                  className="p-2 text-stone-500 hover:text-[#A88A4A] transition-colors"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            {user ? (
              <Link to={user.role === "super_admin" ? "/admin" : user.role === "buyer" ? "/b2b" : "/area-utente"} data-testid="nav-account" className="inline-flex items-center gap-2 px-4 py-2.5 border border-stone-900/30 text-stone-900 hover:bg-stone-900 hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors">
                <User className="h-3.5 w-3.5" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 border border-stone-900/30 text-stone-900 hover:bg-stone-900 hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors">
                  Accedi
                </Link>
                <Link to="/register" data-testid="nav-register" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 bg-stone-900 hover:bg-[#7A1C2B] text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors">
                  Registrati
                </Link>
              </>
            )}
            <Link to="/register" data-testid="nav-cta" className="inline-flex items-center gap-2 px-4 md:px-5 py-2.5 bg-[#C9A961] hover:bg-[#A88A4A] text-white text-[10px] uppercase tracking-[0.25em] font-bold transition-colors">
              Diventa Partner
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2 text-stone-900"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#faf7f0] border-t border-[#C9A961]/30">
            <div className="px-6 py-6 space-y-1">
              {links.map((l) => (
                l.href.includes("#")
                  ? <a key={l.href} href={l.href} onClick={anchorClick(l.href)} className="block px-3 py-3 text-sm uppercase tracking-[0.2em] text-stone-800 hover:text-[#A88A4A] font-semibold border-b border-stone-200">{l.label}</a>
                  : <Link key={l.href} to={l.href} className="block px-3 py-3 text-sm uppercase tracking-[0.2em] text-stone-800 hover:text-[#A88A4A] font-semibold border-b border-stone-200">{l.label}</Link>
              ))}
              {!user && (
                <div className="flex gap-3 pt-4">
                  <Link to="/login" className="flex-1 text-center px-3 py-3 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] font-bold">Accedi</Link>
                  <Link to="/register" className="flex-1 text-center px-3 py-3 bg-stone-900 text-white text-xs uppercase tracking-[0.2em] font-bold">Registrati</Link>
                </div>
              )}
              {/* Social row */}
              <div className="flex items-center justify-center gap-4 pt-6 mt-4 border-t border-stone-200">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="p-2.5 border border-stone-300 text-stone-600 hover:text-white hover:bg-[#C9A961] hover:border-[#C9A961] transition-colors"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
      {/* Spacer */}
      <div aria-hidden className="h-16" />
    </>
  );
}
